// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { DeepReadonly } from 'src/app/modules/shared/utils/api-call-state.util';
import {
  FeatureDetailsModel,
  FeatureImages,
} from '../../../../api/remote-api/.portal.generated/models';
import { FeatureDetailsService } from '../../services/feature-details.service';
import { FormatService } from '../../services/format.service';

@Component({
  selector: 'app-feature-details',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './feature-details.component.html',
  styleUrls: ['./feature-details.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FeatureDetailsComponent implements OnInit, OnDestroy {
  @Input() isWhatsNewModalVisible = false;
  featureList: FeatureDetailsModel[] = [];
  currentVersion = '';
  currentYear = '';
  selectedFeatureTitle = '';
  selectedFeatureDescription = '';
  currentFeatureOrder = 0;
  previousFeatureOrder = 0;
  featurePCImages: string[] = [];
  featureTabletImages: string[] = [];
  private readonly destroy$ = new Subject<void>();
  private featureImagesSubject = new Subject<{
    version: string;
    featureFolderName: string;
  }>();

  constructor(
    protected featureDetailsService: FeatureDetailsService,
    public screenLayoutService: ScreenLayoutService,
    protected readonly formatService: FormatService
  ) {}

  ngOnInit(): void {
    this.featureDetailsService.getStoredSesoData().subscribe((sesoData) => {
      this.currentYear = sesoData.SesoReleasedYear;
      this.currentVersion = sesoData.SesoReleasedVersion;
      this.getFeatures(this.currentYear, this.currentVersion);
    });

    this.getFeatureImageListSubscribe();
    this.featureDetailsService.showFeatureDetailsModal.subscribe((value) => {
      this.isWhatsNewModalVisible = value;
      if (this.featureList.length > 0) {
        this.getFeatureDetail(this.featureList[0]);
      }
    });
  }

  // created by AI - begin
  private getFeatureImageListSubscribe() {
    this.featureImagesSubject
      .pipe(
        switchMap(({ version, featureFolderName }) =>
          this.featureDetailsService.featureImageList.request({
            year: this.currentYear,
            version,
            featureFolderName,
          })
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((featureImages: DeepReadonly<FeatureImages>) => {
        this.featurePCImages = [...(featureImages.pcImages ?? [])];
        this.featureTabletImages = [...(featureImages.tabletImages ?? [])];
      });
  }
  // created by AI - end

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private clearPreviousVersionData(): void {
    this.featureList = [];
    this.featurePCImages = [];
    this.featureTabletImages = [];
    this.selectedFeatureTitle = '';
    this.selectedFeatureDescription = '';
  }

  getFeatures(year: string, version: string): void {
    this.clearPreviousVersionData();
    this.featureDetailsService.featureListForSelectedVersion
      .request({ year, version })
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => {
        if (Array.isArray(list)) {
          this.featureList = [...list] as FeatureDetailsModel[];
        }
        if (this.featureList.length > 0) {
          this.getFeatureDetail(this.featureList[0]);
        }
      });
  }

  getFeatureDetail(feature: FeatureDetailsModel): void {
    this.previousFeatureOrder = this.currentFeatureOrder;
    this.currentFeatureOrder = Number(feature.featureOrder ?? '0');
    if (
      this.selectedFeatureTitle === '' ||
      this.previousFeatureOrder !== this.currentFeatureOrder
    ) {
      this.getFeatureImages(
        feature.releaseVersion ?? '',
        feature.featureFolderName ?? ''
      );
      this.selectedFeatureTitle = feature.featureTitle ?? '';
      this.selectedFeatureDescription = feature.featureDetails ?? '';
    }
  }

  closeWhatsNewModal(): void {
    this.isWhatsNewModalVisible = false;
  }

  private getFeatureImages(version: string, featureFolderName: string): void {
    this.featurePCImages = [];
    this.featureTabletImages = [];
    this.featureImagesSubject.next({ version, featureFolderName });
  }

  formatFeatureDescription(details: string): string {
    return this.formatService.formatFeatureDescription(details);
  }
}
